import { useMemo } from 'react'
import { useGetOrgCredentialType } from '@/service-library/hooks/org-credential-types'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

export type RollupKeyField = {
  infoTypeId: string
  label: string
}

type UseRollupKeyFieldsOptions = {
  enabled?: boolean
}

/**
 * The line-item (sub-grid) fields eligible to be part of a rollup config's
 * grouping key: fields on a line items table whose info type is in the
 * credential type's `rollup_key_info_types` allowlist. Fields on the header
 * grid don't count — rollup groups line items, not header data. Ordered by
 * the grid field's `sort_order` so the Configs tab's grouping-key pills match
 * field order elsewhere in the app.
 *
 * Grids and fields come from the project context rather than their own query —
 * ProjectLayout already fetches `project_grids` with `project_grid_fields`, so
 * this needs no request of its own.
 */
export default function useRollupKeyFields({
  enabled = true,
}: UseRollupKeyFieldsOptions = {}) {
  const { project } = useProjectContext()

  const { orgCredentialType, isPending: isPendingCredentialType } =
    useGetOrgCredentialType({
      id: project.org_credential_type_id || '',
      filters: {
        fields__include: 'rollup_key_info_types_ids',
      },
      enabled: enabled && !!project.org_credential_type_id,
    })

  const keyFields = useMemo<RollupKeyField[]>(() => {
    const allowlist = new Set(
      orgCredentialType?.rollup_key_info_types_ids ?? [],
    )
    if (allowlist.size === 0) return []

    const eligibleFields = (project.project_grids ?? [])
      .filter((grid) => grid.parent_project_grid_id !== null)
      .flatMap((grid) => grid.project_grid_fields ?? [])
      .filter(
        (field) => field.info_type_id && allowlist.has(field.info_type_id),
      )
      .sort((a, b) => a.sort_order - b.sort_order)

    // An info type is unique within a project — the field pickers disable one
    // that's already in use anywhere in it. A duplicate reaching here means
    // the project is misconfigured, so report it instead of quietly hiding it.
    // It's still collapsed, because the API requires key_info_types to be
    // unique and would reject a config carrying the same one twice.
    const labelByInfoTypeId = new Map<string, string>()
    const fields: RollupKeyField[] = []
    eligibleFields.forEach((field) => {
      const infoTypeId = field.info_type_id as string
      const existingLabel = labelByInfoTypeId.get(infoTypeId)
      if (existingLabel !== undefined) {
        console.error(
          `Project ${project.id}: line-item fields "${existingLabel}" and ` +
            `"${field.name}" both use info type ${infoTypeId}. Info types are ` +
            'unique per project, so this project is misconfigured. The rollup ' +
            'grouping key will offer the first field only.',
        )
        return
      }
      labelByInfoTypeId.set(infoTypeId, field.name)
      fields.push({ infoTypeId, label: field.name })
    })
    return fields
  }, [
    orgCredentialType?.rollup_key_info_types_ids,
    project.id,
    project.project_grids,
  ])

  const isPending =
    enabled && !!project.org_credential_type_id && isPendingCredentialType

  return { keyFields, isPending }
}
